import { MailOutlined } from '@ant-design/icons'
import { NetworkStatus } from '@apollo/client'
import { Avatar, Result, Spin } from 'antd'
import cls from 'classnames'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { AppHeader, ColumnLayout } from '@/components'
import { useIsFeatureToggleOn } from '@/components/feature-toggle/feature-toggle.hooks'
import { useGetAnniversaryCardsLazyQuery, useGetHrbpRelatedLeadersQuery, useGetViewerQuery } from '@/generated/graphql'

import { AnniversaryCards } from './anniversary-card-management.component'
import s from './anniversary-card.module.less'

export const AnniversaryCardManagementValidateContainer: React.FC = () => {
  const fetchCount = 1000
  const anniversary = moment().format('YYYY')
  const { t } = useTranslation()
  const isFeatureToggleOn = useIsFeatureToggleOn('anniversaryCard')
  const [user, setUser] = React.useState<{ userId: number; email: string }>()

  const { data: viewerData } = useGetViewerQuery({
    skip: !isFeatureToggleOn,
    variables: {
      withIsAnniversaryCardValidator: true,
    },
  })

  const isAnniversaryCardValidator = viewerData?.viewer.isAnniversaryCardValidator

  const { data: leaderData, loading: leaderLoading } = useGetHrbpRelatedLeadersQuery({
    skip: !isFeatureToggleOn || !isAnniversaryCardValidator,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const [getAnniversaryCards, { data, loading, networkStatus }] = useGetAnniversaryCardsLazyQuery({
    skip: !user,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  React.useEffect(() => {
    if (user) {
      getAnniversaryCards({
        variables: {
          leaderEmail: user.email,
          hrbpEmail: viewer.email,
          creatorUserId: user.userId,
          pagination: {
            last: fetchCount,
          },
          anniversary,
          withFinished: true,
          withUnfinished: true,
          withBlocked: true,
        },
      })
    }
  }, [user])

  if (!isFeatureToggleOn || !viewerData) {
    return null
  }

  const leaders = leaderData?.viewer.hrbpRelatedLeaders?.slice() || []
  const viewer = viewerData.viewer

  const header = (
    <AppHeader
      title={t('anniversaryCard.system.title')}
      avatar={
        <span style={{ fontSize: 24 }}>
          <MailOutlined />
        </span>
      }
    />
  )

  const leaderHeader = (
    <div className={s.leaderHeader}>
      {leaders.map((leader) => (
        <div key={leader.userId} className={s.leader}>
          <Avatar
            className={cls(s.avatar, { [s.selected]: leader.userId === user?.userId })}
            onClick={() => setUser({ userId: leader.userId, email: leader.email })}
            src={leader.avatarUrl}
          />
          <span className={cls(s.name, { [s.selected]: leader.userId === user?.userId })}>{leader.name}</span>
        </div>
      ))}
    </div>
  )

  const container = isAnniversaryCardValidator ? (
    <div className={s.container}>
      {leaderLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <Spin size='large' delay={300} />
        </div>
      ) : (
        leaderHeader
      )}
      {user && (
        <>
          <AnniversaryCards
            data={data}
            creatorUserId={user.userId}
            anniversary={anniversary}
            key='unfinished'
            type='unfinished'
            loading={loading}
          />
          <AnniversaryCards
            data={data}
            creatorUserId={user.userId}
            anniversary={anniversary}
            key='finished'
            type='finished'
            loading={loading}
          />
          <AnniversaryCards
            data={data}
            creatorUserId={user.userId}
            anniversary={anniversary}
            key='blocked'
            type='blocked'
            loading={networkStatus === NetworkStatus.loading}
          />
        </>
      )}
    </div>
  ) : (
    <Result status='warning' title='无权限处理周年贺卡' />
  )

  return <ColumnLayout header={header} middle={container} />
}
